import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSwitcher from '../components/LanguageSwitcher';

const LAST_UPDATED = '15 April 2026';
const CONTROLLER = 'nordX Labs Oy';
const BUSINESS_ID = '3615236-4';
const CONTACT = 'hello@animlearn.com';

const content = {
  en: {
    title: 'Privacy Policy',
    updated: `Last updated: ${LAST_UPDATED}`,
    back: 'Back',
    sections: [
      {
        title: '1. Data Controller',
        body: `The data controller for AnimLearn is:\n\n**${CONTROLLER}**\nBusiness ID (Y-tunnus): ${BUSINESS_ID}\nEmail: ${CONTACT}`,
      },
      {
        title: '2. What Data We Collect',
        table: [
          ['Account', 'Full name, email address, preferred language, account creation date'],
          ['Google Sign-In (optional)', 'Google user ID, profile picture URL, name and email from Google OAuth'],
          ['Learning activity', 'Video watch percentage, concept mastery scores, practice attempts and answers, AI feedback on submissions, time taken on exercises'],
          ['Chat history', 'Problem-solving conversations with the AI tutor (Pixel AI)'],
          ['Security logs', 'IP address, browser user-agent — recorded on sign-in/sign-up for fraud prevention. Retained for 90 days, then deleted automatically.'],
        ],
      },
      {
        title: '3. Legal Basis for Processing',
        body: `Processing of personal data in AnimLearn is based on **public interest and the exercise of official authority (GDPR Article 6(1)(e))**, in connection with the school's educational activities.\n\nSecurity logging (IP address, user-agent) is based on our **legitimate interest (Article 6(1)(f))** in preventing unauthorised access and fraud.`,
      },
      {
        title: '4. Profiling and Automated Processing',
        body: 'AnimLearn automatically analyses your learning activity to personalise the educational experience. This includes:',
        bullets: [
          'Calculating a mastery score (0–1) per concept based on your quiz answers and practice attempts',
          'Tracking a learning trend (improving / stable / declining) per concept',
          'Monitoring video watch completion to identify gaps in learning',
          'Generating personalised practice questions based on your performance',
        ],
        body2: 'This constitutes **profiling under GDPR Article 4(4)**. The profiling is carried out in the public interest as part of educational activities. It does not produce legal effects or significantly affect you.',
      },
      {
        title: '5. Third-Party Processors',
        body: 'We share data with the following third-party service providers acting as data processors:',
        table: [
          ['Google LLC', 'Google OAuth authentication. Data transferred: name, email, Google user ID. Governed by Google\'s Data Processing Terms.'],
          ['Anthropic / Claude (via Google Cloud Vertex AI, EU)', 'AI content generation. Learning prompts and concept content are processed by Claude, hosted on Google Cloud infrastructure within the European Union. No student personal identifiers are sent to the model.'],
          ['Microsoft Azure (EU)', 'AI embeddings for syllabus search (Azure OpenAI). Document text is processed. Hosted in EU region.'],
          ['Cloudflare R2 (EU)', 'Storage of generated video files and SVG assets.'],
          ['Supabase / PostgreSQL', 'Database hosting for all user and learning data.'],
        ],
        body2: 'We do not sell personal data to any third party.',
      },
      {
        title: '6. Data Retention',
        table: [
          ['Account data', 'Retained while your account is active. Deleted on account deletion request.'],
          ['Learning activity', 'Retained while your account is active. Deleted on account deletion.'],
          ['Security logs (IP, user-agent)', 'Automatically deleted after 90 days.'],
          ['AI-generated videos', 'Retained while linked to your account. Deleted on account deletion.'],
        ],
      },
      {
        title: '7. Your Rights',
        body: 'Under GDPR, you have the following rights:',
        bullets: [
          'Right of access — request a copy of your personal data',
          'Right to rectification — correct inaccurate data via Settings',
          'Right to erasure — delete your account and all associated data via Settings → Delete Account',
          'Right to restriction — request that we limit processing of your data',
          'Right to object — object to processing based on public interest',
          'Right to data portability — request your data in a machine-readable format',
        ],
        body2: `To exercise any of these rights, contact us at ${CONTACT}. We will respond within 30 days.\n\nYou also have the right to lodge a complaint with the Finnish Data Protection Ombudsman (tietosuoja.fi).`,
      },
      {
        title: '8. Data Security',
        body: 'We implement the following security measures:',
        bullets: [
          'Passwords are hashed using bcrypt (never stored in plaintext)',
          'All data in transit is encrypted with TLS/HTTPS',
          'Authentication tokens are stored locally in the browser and not sent to third parties',
          'Access to production databases is restricted to authorised personnel',
        ],
      },
      {
        title: '9. Cookies and Local Storage',
        body: 'AnimLearn does not use tracking cookies. Authentication tokens are stored in your browser\'s **localStorage** and are used only to keep you logged in. No third-party analytics or advertising cookies are used.',
      },
      {
        title: '10. Children\'s Data',
        body: 'AnimLearn is designed for use in schools, including by minors. Student accounts are created and managed within the school context. Teachers and school administrators are responsible for ensuring that student accounts comply with applicable laws on processing children\'s data.\n\nWe do not knowingly collect data from children outside of a supervised educational context.',
      },
      {
        title: '11. Changes to This Policy',
        body: 'We may update this privacy policy from time to time. When we do, we will update the "Last updated" date at the top of this page. Material changes will be communicated via the application or by email.',
      },
      {
        title: '12. Contact',
        body: `For privacy-related questions, contact:\n**${CONTROLLER}**\nEmail: ${CONTACT}`,
      },
    ],
  },

  fi: {
    title: 'Tietosuojakäytäntö',
    updated: `Päivitetty viimeksi: ${LAST_UPDATED}`,
    back: 'Takaisin',
    sections: [
      {
        title: '1. Rekisterinpitäjä',
        body: `AnimLearnin rekisterinpitäjä on:\n\n**${CONTROLLER}**\nY-tunnus: ${BUSINESS_ID}\nSähköposti: ${CONTACT}`,
      },
      {
        title: '2. Kerättävät henkilötiedot',
        table: [
          ['Tilitiedot', 'Koko nimi, sähköpostiosoite, kielivalinta, tilin luontipäivä'],
          ['Google-kirjautuminen (valinnainen)', 'Google-käyttäjätunnus, profiilikuvan URL, nimi ja sähköposti Google OAuth -palvelusta'],
          ['Oppimistoiminta', 'Videon katseluprosentti, käsitteen hallintapisteet, harjoitusten yritykset ja vastaukset, tekoälypalaute palautuksista, tehtäviin käytetty aika'],
          ['Keskusteluhistoria', 'Ongelmanratkaisukeskustelut tekoälytutorin kanssa (Pixel AI)'],
          ['Tietoturvaloki', 'IP-osoite ja selaimen käyttäjäagentti — tallennetaan kirjautumisen yhteydessä väärinkäytösten estämiseksi. Poistetaan automaattisesti 90 päivän kuluttua.'],
        ],
      },
      {
        title: '3. Käsittelyn oikeusperuste',
        body: `Henkilötietojen käsittely AnimLearnissa perustuu **yleiseen etuun ja julkisen vallan käyttöön (GDPR 6 artiklan 1 kohdan e alakohta)** koulun opetustehtävän yhteydessä.\n\nTietoturvalokien (IP-osoite, käyttäjäagentti) käsittely perustuu **oikeutettuun etuumme (6 artiklan 1 kohdan f alakohta)** luvattoman käytön ja petosyritysten estämiseksi.`,
      },
      {
        title: '4. Profilointi ja automatisoitu käsittely',
        body: 'AnimLearn analysoi automaattisesti oppimistoimintaasi oppimiskokemuksen personalisoimiseksi. Tämä sisältää:',
        bullets: [
          'Hallintapisteen laskemisen (0–1) jokaiselle käsitteelle tietokilpailu- ja harjoitusvastausten perusteella',
          'Oppimistrendin seuraaminen (kehittyvä / vakaa / heikkenevä) käsitteittäin',
          'Videokatsomisen seuranta oppimisaukkojen tunnistamiseksi',
          'Yksilöllisten harjoitustehtävien luominen suorituksesi perusteella',
        ],
        body2: 'Tämä on **GDPR:n 4 artiklan 4 kohdan mukaista profilointia**. Profilointi toteutetaan yleisen edun nimissä osana opetustehtävää. Se ei aiheuta oikeudellisia vaikutuksia eikä merkittävästi vaikuta sinuun.',
      },
      {
        title: '5. Kolmansien osapuolten käsittelijät',
        body: 'Jaamme tietoja seuraavien alihankkijoiden kanssa:',
        table: [
          ['Google LLC', 'Google OAuth -tunnistautuminen. Siirrettävät tiedot: nimi, sähköposti, Google-käyttäjätunnus. Googlen tietojenkäsittelyehtojen alainen.'],
          ['Anthropic / Claude (Google Cloud Vertex AI, EU)', 'Tekoälypohjainen sisällöntuotanto. Oppimispromptit ja käsitesisältö käsitellään Claude-mallilla, joka toimii Google Cloudin infrastruktuurissa Euroopan unionin alueella. Opiskelijoiden henkilökohtaisia tunnistetietoja ei lähetetä mallille.'],
          ['Microsoft Azure (EU)', 'Tekoälypohjainen upottaminen oppimateriaalihaun tueksi (Azure OpenAI). Dokumenttiteksti käsitellään EU:n alueella.'],
          ['Cloudflare R2 (EU)', 'Generoitujen videoiden ja SVG-resurssien tallennus.'],
          ['Supabase / PostgreSQL', 'Kaikkien käyttäjä- ja oppimistietojen tietokantapalvelu.'],
        ],
        body2: 'Emme myy henkilötietoja kolmansille osapuolille.',
      },
      {
        title: '6. Tietojen säilytysajat',
        table: [
          ['Tilitiedot', 'Säilytetään tilin voimassaoloajan. Poistetaan tilin poistamispyynnön yhteydessä.'],
          ['Oppimistoiminta', 'Säilytetään tilin voimassaoloajan. Poistetaan tilin poistamisen yhteydessä.'],
          ['Tietoturvaloki (IP, käyttäjäagentti)', 'Poistetaan automaattisesti 90 päivän kuluttua.'],
          ['Tekoälygeneroidut videot', 'Säilytetään niin kauan kuin ne on linkitetty tiliin. Poistetaan tilin poistamisen yhteydessä.'],
        ],
      },
      {
        title: '7. Rekisteröidyn oikeudet',
        body: 'GDPR:n mukaan sinulla on seuraavat oikeudet:',
        bullets: [
          'Oikeus saada pääsy tietoihin — pyydä kopio henkilötiedoistasi',
          'Oikeus tietojen oikaisemiseen — korjaa virheelliset tiedot Asetuksissa',
          'Oikeus tietojen poistamiseen — poista tilisi ja kaikki siihen liittyvät tiedot Asetukset → Poista tili -toiminnolla',
          'Oikeus käsittelyn rajoittamiseen — pyydä meitä rajoittamaan tietojesi käsittelyä',
          'Vastustamisoikeus — vastusta yleiseen etuun perustuvaa käsittelyä',
          'Oikeus tietojen siirrettävyyteen — pyydä tietosi koneluettavassa muodossa',
        ],
        body2: `Oikeuksien käyttämiseksi ota yhteyttä: ${CONTACT}. Vastaamme 30 päivän kuluessa.\n\nVoit myös tehdä valituksen tietosuojavaltuutetulle (tietosuoja.fi).`,
      },
      {
        title: '8. Tietoturva',
        body: 'Käytämme seuraavia tietoturvatoimia:',
        bullets: [
          'Salasanat hajautetaan bcrypt-algoritmilla (ei koskaan tallennettu selväkielisenä)',
          'Kaikki siirrettävä data salataan TLS/HTTPS-protokollalla',
          'Tunnistustokenit tallennetaan paikallisesti selaimeen eikä niitä lähetetä kolmansille osapuolille',
          'Tuotantotietokantoihin pääsy on rajattu valtuutetuille henkilöille',
        ],
      },
      {
        title: '9. Evästeet ja paikallinen tallennus',
        body: 'AnimLearn ei käytä seurantaevästeitä. Tunnistustokenit tallennetaan selaimen **localStorageen** ja niitä käytetään ainoastaan kirjautuneena pysymiseen. Kolmansien osapuolten analytiikka- tai mainosevästeitä ei käytetä.',
      },
      {
        title: '10. Lasten tiedot',
        body: 'AnimLearn on suunniteltu käytettäväksi kouluissa, myös alaikäisten toimesta. Opiskelijatilit luodaan ja hallinnoidaan koulun toimesta. Opettajat ja koulun ylläpitäjät vastaavat siitä, että opiskelijatilit täyttävät lasten tietojen käsittelyä koskevat lakisääteiset vaatimukset.\n\nEmme tietoisesti kerää tietoja lapsilta koulun valvoman toiminnan ulkopuolella.',
      },
      {
        title: '11. Tietosuojakäytännön muutokset',
        body: 'Saatamme päivittää tätä tietosuojakäytäntöä ajoittain. Päivityksen yhteydessä muutamme sivun yläosassa olevaa päivämäärää. Merkittävistä muutoksista ilmoitetaan sovelluksen kautta tai sähköpostitse.',
      },
      {
        title: '12. Yhteystiedot',
        body: `Tietosuojaan liittyvissä kysymyksissä ota yhteyttä:\n**${CONTROLLER}**\nSähköposti: ${CONTACT}`,
      },
    ],
  },

  sv: {
    title: 'Integritetspolicy',
    updated: `Senast uppdaterad: ${LAST_UPDATED}`,
    back: 'Tillbaka',
    sections: [
      {
        title: '1. Personuppgiftsansvarig',
        body: `Personuppgiftsansvarig för AnimLearn är:\n\n**${CONTROLLER}**\nFöretagsnummer (FO-nummer): ${BUSINESS_ID}\nE-post: ${CONTACT}`,
      },
      {
        title: '2. Vilka uppgifter vi samlar in',
        table: [
          ['Konto', 'Fullständigt namn, e-postadress, språkpreferens, datum för kontokapande'],
          ['Google-inloggning (valfri)', 'Google-användar-ID, profilbildens URL, namn och e-post via Google OAuth'],
          ['Inlärningsaktivitet', 'Videons tittarprocent, mastringspäng per begrepp, övningsförsök och svar, AI-feedback på inlämningar, tid spenderad på övningar'],
          ['Chatthistorik', 'Problemlösningskonversationer med AI-tutorn (Pixel AI)'],
          ['Säkerhetsloggar', 'IP-adress och webbläsarens user-agent — registreras vid inloggning för att förhindra bedrägerier. Raderas automatiskt efter 90 dagar.'],
        ],
      },
      {
        title: '3. Rättslig grund för behandling',
        body: `Behandlingen av personuppgifter i AnimLearn grundar sig på **allmänt intresse och utövandet av offentlig makt (GDPR artikel 6.1 e)** inom ramen för skolans utbildningsverksamhet.\n\nSäkerhetsloggning (IP-adress, user-agent) grundar sig på vårt **berättigade intresse (artikel 6.1 f)** att förhindra obehörig åtkomst och bedrägeri.`,
      },
      {
        title: '4. Profilering och automatiserad behandling',
        body: 'AnimLearn analyserar automatiskt din inlärningsaktivitet för att personalisera lärupplevelsen. Detta inkluderar:',
        bullets: [
          'Beräkning av ett mastringsvärde (0–1) per begrepp baserat på dina quiz-svar och övningsförsök',
          'Spårning av inlärningstrender (förbättrande / stabil / försämrande) per begrepp',
          'Övervakning av videogenomförande för att identifiera kunskapsluckor',
          'Generering av personaliserade övningsfrågor baserat på din prestation',
        ],
        body2: 'Detta utgör **profilering enligt GDPR artikel 4.4**. Profileringen sker i allmänhetens intresse som en del av utbildningsverksamheten. Den medför inga rättsliga konsekvenser eller påverkar dig på ett liknande betydande sätt.',
      },
      {
        title: '5. Tredjepartsbehandlare',
        body: 'Vi delar uppgifter med följande underleverantörer:',
        table: [
          ['Google LLC', 'Google OAuth-autentisering. Uppgifter som överförs: namn, e-post, Google-användar-ID. Regleras av Googles villkor för databehandling.'],
          ['Anthropic / Claude (via Google Cloud Vertex AI, EU)', 'AI-baserad innehållsgenerering. Inlärningspromptar och begreppsinnehåll behandlas av Claude-modellen, som körs på Google Clouds infrastruktur inom EU. Inga personliga identifierare för studenter skickas till modellen.'],
          ['Microsoft Azure (EU)', 'AI-inbäddningar för syllabus-sökning (Azure OpenAI). Dokumenttext behandlas inom EU-regionen.'],
          ['Cloudflare R2 (EU)', 'Lagring av genererade videofiler och SVG-tillgångar.'],
          ['Supabase / PostgreSQL', 'Databashosting för alla användar- och inlärningsdata.'],
        ],
        body2: 'Vi säljer inte personuppgifter till någon tredje part.',
      },
      {
        title: '6. Lagringstider',
        table: [
          ['Kontouppgifter', 'Lagras så länge kontot är aktivt. Raderas vid begäran om kontoborttagning.'],
          ['Inlärningsaktivitet', 'Lagras så länge kontot är aktivt. Raderas vid kontoborttagning.'],
          ['Säkerhetsloggar (IP, user-agent)', 'Raderas automatiskt efter 90 dagar.'],
          ['AI-genererade videor', 'Lagras så länge de är kopplade till kontot. Raderas vid kontoborttagning.'],
        ],
      },
      {
        title: '7. Dina rättigheter',
        body: 'Enligt GDPR har du följande rättigheter:',
        bullets: [
          'Rätt till tillgång — begär en kopia av dina personuppgifter',
          'Rätt till rättelse — korrigera felaktiga uppgifter via Inställningar',
          'Rätt till radering — ta bort ditt konto och alla tillhörande uppgifter via Inställningar → Ta bort konto',
          'Rätt till begränsning — begär att vi begränsar behandlingen av dina uppgifter',
          'Rätt att invända — invända mot behandling baserad på allmänt intresse',
          'Rätt till dataportabilitet — begär dina uppgifter i maskinläsbart format',
        ],
        body2: `För att utöva dessa rättigheter, kontakta oss på ${CONTACT}. Vi svarar inom 30 dagar.\n\nDu har också rätt att lämna in ett klagomål till den finska dataombudsmannen (tietosuoja.fi).`,
      },
      {
        title: '8. Datasäkerhet',
        body: 'Vi tillämpar följande säkerhetsåtgärder:',
        bullets: [
          'Lösenord hashas med bcrypt (lagras aldrig i klartext)',
          'All data under överföring krypteras med TLS/HTTPS',
          'Autentiseringstoken lagras lokalt i webbläsaren och skickas inte till tredje part',
          'Åtkomst till produktionsdatabaser är begränsad till behörig personal',
        ],
      },
      {
        title: '9. Cookies och lokal lagring',
        body: 'AnimLearn använder inga spårningscookies. Autentiseringstoken lagras i webbläsarens **localStorage** och används enbart för att hålla dig inloggad. Inga tredjepartsanalys- eller reklamcookies används.',
      },
      {
        title: '10. Barns uppgifter',
        body: 'AnimLearn är utformat för användning i skolor, inklusive av minderåriga. Elevkonton skapas och hanteras inom skolans kontext. Lärare och skoladministratörer ansvarar för att elevkonton uppfyller tillämplig lagstiftning om behandling av barns uppgifter.\n\nVi samlar inte medvetet in uppgifter från barn utanför en övervakad utbildningskontext.',
      },
      {
        title: '11. Ändringar i denna policy',
        body: 'Vi kan uppdatera denna integritetspolicy från tid till annan. När vi gör det uppdaterar vi datumet "Senast uppdaterad" längst upp på sidan. Väsentliga ändringar kommuniceras via applikationen eller via e-post.',
      },
      {
        title: '12. Kontakt',
        body: `För integritetsfrågor, kontakta:\n**${CONTROLLER}**\nE-post: ${CONTACT}`,
      },
    ],
  },
};

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const c = content[language] || content.en;

  return (
    <div style={{ minHeight: '100vh', background: '#f8f8fc', fontFamily: 'Inter, sans-serif' }}>
      {/* Header */}
      <div style={{ background: '#fff', borderBottom: '1px solid #ece8f5', padding: '1rem 2rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button onClick={() => navigate(-1)}
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: 'none', border: 'none', cursor: 'pointer', color: '#5B35A0', fontWeight: 500, fontSize: '0.9rem' }}>
            <ArrowLeft size={16} /> {c.back}
          </button>
          <span style={{ color: '#ccc' }}>|</span>
          <span style={{ fontWeight: 700, color: '#1a1a2e', fontSize: '1rem' }}>AnimLearn — {c.title}</span>
        </div>
        <LanguageSwitcher />
      </div>

      <div style={{ maxWidth: 780, margin: '0 auto', padding: '3rem 2rem 5rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 800, color: '#1a1a2e', marginBottom: '0.4rem' }}>{c.title}</h1>
        <p style={{ color: '#888', fontSize: '0.88rem', marginBottom: '2.5rem' }}>{c.updated}</p>

        {c.sections.map((s, i) => (
          <Section key={i} title={s.title}>
            {s.body && <RichText text={s.body} />}
            {s.table && <Table rows={s.table} />}
            {s.bullets && <ul style={{ paddingLeft: '1.4rem', lineHeight: 2, margin: '0.5rem 0' }}>{s.bullets.map((b, j) => <li key={j}>{b}</li>)}</ul>}
            {s.body2 && <RichText text={s.body2} style={{ marginTop: '0.75rem' }} />}
          </Section>
        ))}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: '2.5rem' }}>
      <h2 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#5B35A0', marginBottom: '0.75rem', borderBottom: '2px solid #ece8f5', paddingBottom: '0.4rem' }}>
        {title}
      </h2>
      <div style={{ color: '#333', fontSize: '0.93rem', lineHeight: 1.75 }}>
        {children}
      </div>
    </div>
  );
}

function RichText({ text, style }) {
  // Bold **text** and newlines
  const parts = text.split(/(\*\*[^*]+\*\*|\n)/g);
  return (
    <p style={{ margin: '0.3rem 0', ...style }}>
      {parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) return <strong key={i}>{part.slice(2, -2)}</strong>;
        if (part === '\n') return <br key={i} />;
        return part;
      })}
    </p>
  );
}

function Table({ rows }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '0.75rem', fontSize: '0.88rem' }}>
      <tbody>
        {rows.map(([label, value], i) => (
          <tr key={i} style={{ borderBottom: '1px solid #ede9f8' }}>
            <td style={{ padding: '0.6rem 1rem 0.6rem 0', fontWeight: 600, color: '#1a1a2e', verticalAlign: 'top', width: '32%' }}>{label}</td>
            <td style={{ padding: '0.6rem 0', color: '#555', verticalAlign: 'top' }}>{value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
